import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { api } from "@/lib/api/client";
import { useAuthV2 } from "@/lib/authv2/store";
import { FALLBACK_BRANCHES, FALLBACK_REGIONS } from "./fallback";
import { ALL_SCOPE } from "./scopeCodes";
import { type Region, type TreeUnit, regionOfBranchIn, regionsFromUnits } from "./tree";
import { serverErrorMessage } from "@/lib/api/error-message";

export type BranchOption = {
  value: string;
  label: string;
  shortLabel: string;
  isActive: boolean;
  kind: "group" | "branch";
};

/**
 * Narrows a unit list to the branches assigned to the signed-in user.
 *
 * Only the `casdoor` source carries a real assignment. Under `database` and
 * `shadow` the set is derived from role and department, so filtering on it
 * would hide branches from people who currently see them.
 *
 * An empty assignment does NOT filter. Empty means "assigned nothing" and "the
 * session has not hydrated yet" equally, and the two are indistinguishable
 * here. Hiding everything on the second turns a race into a screen that looks
 * broken. The backend denies the data either way, so the worst case is a picker
 * whose choices return nothing — legible, and reportable.
 *
 * Group nodes always survive: region tabs need them to render, and a group with
 * every leaf filtered out is empty on its own.
 *
 * `keepValue` pins the branch a form is already editing. Without it, opening an
 * out-of-scope record and saving would rewrite its branch to whatever the
 * select falls back to.
 */
function useScopedUnits(units: TreeUnit[], keepValue?: string | null): TreeUnit[] {
  const scope = useAuthV2((s) => s.branchScope);
  return useMemo(() => {
    if (!scope || scope.source !== "casdoor") return units;
    const allowed = new Set(scope.branches);
    if (allowed.size === 0) return units;
    return units.filter(
      (unit) => unit.kind === "group" || allowed.has(unit.code) || unit.code === keepValue,
    );
  }, [keepValue, scope, units]);
}

/**
 * The branch catalog, shared by every feature that shows a branch.
 *
 * One query key so many components mount without multiplying requests, and a
 * long staleTime because the catalog changes rarely — a director adding a
 * branch is a weekly event at most, and a page refresh picks it up.
 *
 * Two lists come back, and picking the wrong one fails quietly:
 *
 * - `units` is narrowed to the caller's assigned branches. Use it for anything
 *   that offers a choice.
 * - `allUnits` is the whole catalog. Use it to resolve a code that already
 *   exists on a record — a label, a region — because an old order may name a
 *   branch the current user is not assigned to, and it still has to display.
 */
export function useBranchCatalog(opts?: { keepValue?: string | null }) {
  const query = useQuery({
    queryKey: ["branches"],
    queryFn: async () => {
      const { data, error } = await api.GET("/branches", {});
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh mục chi nhánh"));
      return data;
    },
    staleTime: 5 * 60_000,
  });

  const units: TreeUnit[] = useMemo(() => {
    if (query.data) return query.data as TreeUnit[];
    // Degrade to the static list rather than an empty screen. Callers surface
    // `isFallback` so nobody acts on a stale list believing it is current.
    // The region groups are included so region tabs still render while the
    // catalog is unreachable.
    return [
      ...FALLBACK_REGIONS.map((region, index) => ({
        code: region.id,
        name: region.label,
        kind: "group" as const,
        isActive: true,
        sortOrder: index,
      })),
      ...FALLBACK_BRANCHES.map((branch) => ({
        code: branch.code,
        name: branch.name,
        shortLabel: branch.shortLabel,
        kind: "branch" as const,
        parentCode: branch.region,
        isActive: true,
      })),
    ];
  }, [query.data]);

  const scopedUnits = useScopedUnits(units, opts?.keepValue);

  return {
    ...query,
    units: scopedUnits,
    allUnits: units,
    isFallback: !query.data && !query.isLoading,
  };
}

/**
 * Branch options for a select or filter.
 *
 * `activeOnly` is the important distinction: creating something new must not
 * offer a closed branch, while a report filter must still show it or history
 * disappears. `keepValue` keeps the currently-selected branch in the list even
 * when deactivated, so editing an existing record cannot silently overwrite it.
 */
export function useBranchOptions(opts?: { activeOnly?: boolean; keepValue?: string | null }) {
  // Tách ra biến trước: caller thường truyền object literal mới mỗi lần render,
  // và dep dạng `opts?.x` khiến React Compiler bỏ luôn memo của khối dưới.
  const activeOnly = opts?.activeOnly ?? false;
  const keepValue = opts?.keepValue ?? null;
  const { units, isFallback, isLoading } = useBranchCatalog({ keepValue });
  const options = useMemo(() => {
    return units
      .filter((unit) => unit.kind === "branch")
      .filter((unit) => {
        if (!activeOnly) return true;
        if (unit.isActive !== false) return true;
        return keepValue != null && unit.code === keepValue;
      })
      .map<BranchOption>((unit) => ({
        value: unit.code,
        label: unit.isActive === false ? `${unit.name} (Ngưng hoạt động)` : unit.name,
        shortLabel: unit.shortLabel || unit.name,
        isActive: unit.isActive !== false,
        kind: "branch",
      }));
  }, [activeOnly, keepValue, units]);

  return { options, isFallback, isLoading };
}

/**
 * Branches in the shape the legacy `BRANCHES` constant had, so existing
 * `.map` and `.find` call sites migrate by swapping the source rather than
 * being rewritten.
 */
export function useBranchList(opts?: { activeOnly?: boolean; keepValue?: string | null }) {
  // Xem ghi chú ở useBranchOptions: dep phải là biến đơn, không phải optional
  // chain trên object mới tạo mỗi render.
  const activeOnly = opts?.activeOnly ?? false;
  const keepValue = opts?.keepValue ?? null;
  const { units, isFallback, isLoading } = useBranchCatalog({ keepValue });
  return useMemo(() => {
    const list = units
      .filter((unit) => unit.kind === "branch")
      .filter((unit) => {
        if (!activeOnly) return true;
        if (unit.isActive !== false) return true;
        return keepValue != null && unit.code === keepValue;
      })
      .map((unit) => ({
        value: unit.code,
        label: unit.name,
        shortLabel: unit.shortLabel || unit.name,
        isActive: unit.isActive !== false,
        // Số ekip song song. Giữ `undefined` khi catalog offline không có —
        // bên dùng phải chịu được absent thay vì nhận một con số đoán bừa
        // (xem ghi chú ở TreeUnit.capacity).
        capacity: unit.capacity,
        // Số kíp riêng từng thương hiệu (QĐ 0523). Khoá tuỳ chọn — chỉ Lịch
        // chụp đọc, các màn khác (và bộ giả của chúng) không phải biết tới.
        ...(unit.ekipByBrand ? { ekipByBrand: unit.ekipByBrand } : {}),
      }));
    return { branches: list, isFallback, isLoading };
  }, [isFallback, isLoading, activeOnly, keepValue, units]);
}

/**
 * Branch label lookup bound to the loaded catalog.
 *
 * Always resolves deactivated branches, and branches outside the caller's
 * assigned scope: a label is about naming a record that already exists, not
 * about offering a choice. Reading the scoped list here would miss, and the
 * fallback below returns the raw code — an old order would render `sg_lvs`
 * instead of the branch name.
 *
 * `staff.branch` may also hold a SCOPE code — "all" or "<group>_all" (decisions
 * 0293 / 0389 allow both) — which is not a catalog unit. Those are named here
 * too (test run 18/10/2026 with accounts carrying such codes: every screen
 * listing them printed "· all"). A `<group>_all` whose group is not in the
 * catalog stays raw, like any unknown code.
 */
export function useBranchLabel(): (
  id: string | null | undefined,
  opts?: { full?: boolean },
) => string {
  const { allUnits } = useBranchCatalog();
  return useMemo(() => {
    const byCode = new Map(allUnits.map((unit) => [unit.code, unit]));
    return (id: string | null | undefined, opts?: { full?: boolean }) => {
      if (!id) return "";
      if (id === ALL_SCOPE) return "Tất cả cơ sở";
      const unit = byCode.get(id);
      if (!unit) {
        const group = id.endsWith("_all") ? byCode.get(id.slice(0, -"_all".length)) : undefined;
        return group?.kind === "group" ? `Toàn ${group.name}` : id;
      }
      return opts?.full ? unit.name : unit.shortLabel || unit.name;
    };
  }, [allUnits]);
}

/**
 * Regions derived from root-level groups; a new group appears without a code
 * change.
 *
 * Always derived from `allUnits`, which is already the fallback list when the
 * fetch has not landed. Branching on a loading flag here would return a
 * hardcoded pair and hide any region beyond those two.
 */
export function useRegions(): { regions: Region[]; isFallback: boolean } {
  const { allUnits, isFallback } = useBranchCatalog();
  const regions = useMemo(() => regionsFromUnits(allUnits), [allUnits]);
  return { regions, isFallback };
}

/**
 * Region lookup bound to the loaded tree, for components.
 *
 * Reads the full catalog. `regionOfBranchIn` resolves through `rootGroupOf`,
 * and a branch missing from the list falls through to guessing from an `hn`/
 * `sg` prefix — so a scoped list would put out-of-scope branches in the wrong
 * region with nothing to indicate it, rather than failing visibly.
 */
export function useRegionOfBranch(): (branch: string | null | undefined) => string {
  const { allUnits } = useBranchCatalog();
  return useMemo(
    () => (branch: string | null | undefined) => regionOfBranchIn(branch, allUnits),
    [allUnits],
  );
}
